import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Box, Divider, IconButton, Tooltip } from "@mui/material";
import { Bold, Italic, Strikethrough, List, ListOrdered, Quote, Link2, Undo2, Redo2 } from "lucide-react";
import { T, TYPE } from "../user/complaints/shared";

// Rich-text editor for email replies. Emits HTML (the server sanitises it
// again) plus the plain-text version used for the thread preview and search.

const Btn = ({ title, onClick, active, disabled, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={onClick} disabled={disabled}
        sx={{ borderRadius: "6px", color: active ? T.brand : T.inkSecondary, bgcolor: active ? T.brandSoft : "transparent", p: 0.6 }}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const escapeHtml = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
/** Plain text (AI drafts, canned responses) -> paragraphs. */
export const textToHtml = (text) =>
  String(text || "").split(/\n{2,}/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`).join("");

/**
 * @param value    html
 * @param onChange ({ html, text }) => void
 */
export default function RichReply({ value, onChange, placeholder, disabled, onSubmit, editorRef, minHeight = 96 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, code: false, horizontalRule: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" } }),
      Placeholder.configure({ placeholder: placeholder || "Type your reply…" }),
    ],
    content: value || "",
    editable: !disabled,
    onUpdate: ({ editor: e }) => onChange?.({ html: e.isEmpty ? "" : e.getHTML(), text: e.getText({ blockSeparator: "\n\n" }).trim() }),
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (onSubmit && event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
          onSubmit();
          return true;
        }
        return false;
      },
    },
  });

  // external resets (after send, AI draft, canned response)
  useEffect(() => {
    if (editor && value !== undefined && value !== (editor.isEmpty ? "" : editor.getHTML())) editor.commands.setContent(value || "", false);
  }, [value, editor]);
  useEffect(() => {
    if (editorRef) editorRef.current = editor;
  }, [editor, editorRef]);
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (!url) editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else if (/^(https?:|mailto:)/i.test(url)) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  if (!editor) return null;
  return (
    <Box sx={{ border: `1px solid ${T.borderStrong}`, borderRadius: "10px", bgcolor: "#fff", "&:focus-within": { borderColor: T.brand, boxShadow: `0 0 0 3px ${T.brandSoft}` } }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25, px: 0.75, py: 0.5, borderBottom: `1px solid ${T.border}` }}>
        <Btn title="Bold (Ctrl+B)" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></Btn>
        <Btn title="Italic (Ctrl+I)" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></Btn>
        <Btn title="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={15} /></Btn>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <Btn title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={15} /></Btn>
        <Btn title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15} /></Btn>
        <Btn title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={15} /></Btn>
        <Btn title="Link" active={editor.isActive("link")} onClick={setLink}><Link2 size={15} /></Btn>
        <Box sx={{ flexGrow: 1 }} />
        <Btn title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 size={15} /></Btn>
        <Btn title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 size={15} /></Btn>
      </Box>
      <Box sx={{
        px: 1.5, py: 1, minHeight, maxHeight: 320, overflowY: "auto", cursor: "text", fontSize: TYPE.body, color: T.ink, lineHeight: 1.6,
        "& p": { my: 0.5 }, "& ul,& ol": { pl: 3, my: 0.5 }, "& blockquote": { borderLeft: `3px solid ${T.border}`, pl: 1.5, ml: 0, color: T.inkSecondary }, "& a": { color: T.brand },
        "& .ProseMirror": { outline: "none", minHeight: minHeight - 16 },
        "& .ProseMirror p.is-editor-empty:first-of-type::before": { content: "attr(data-placeholder)", color: T.inkMuted, float: "left", height: 0, pointerEvents: "none" },
      }} onClick={() => editor.chain().focus().run()}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
